import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Container,
} from "@mui/material";
import { ArrowBack, Download, Print, Receipt } from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";

export const InvGRNView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGRN();
  }, [id]);

  const fetchGRN = async () => {
    try {
      const response = await purchaseAPI.getGRN(id);
      setGrn(response.data.data.grn || response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching GRN", { variant: "error" });
      navigate(`/hospital/hospital-inventory/purchases/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    if (!grn || !grn.items) return { subtotal: 0, tax: 0, total: 0 };

    const subtotal = grn.items.reduce((sum, item) => {
      const quantity = item.receivedQuantity || item.acceptedQuantity || 0;
      const cost = item.unitCost || 0;
      return sum + quantity * cost;
    }, 0);

    const tax = subtotal * 0.18; // 18% tax
    const total = subtotal + tax;

    return { subtotal, tax, total };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    try {
      const response = await purchaseAPI.downloadGRN(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GRN-${grn.grnNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      enqueueSnackbar("GRN downloaded successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Error downloading GRN", { variant: "error" });
    }
  };

  if (loading) return <Typography variant="h5" sx={{ p: 3 }}>Loading GRN Details...</Typography>;
  if (!grn) return <Typography variant="h5" sx={{ p: 3 }}>GRN not found</Typography>;

  const { subtotal, tax, total } = calculateTotals();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          p: 3,
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
        className="no-print"
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/hospital/hospital-inventory/purchases/${id}`)}
          variant="outlined"
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Back to Purchase Order
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<Print />}
            onClick={handlePrint}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2 }}
          >
            Print
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleDownload}
            variant="contained"
            size="large"
            sx={{ borderRadius: 2 }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Main GRN Card */}
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header with Icon */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Receipt sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
            <Box>
              <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                Goods Receipt Note
              </Typography>
              <Typography variant="h5" color="text.secondary" fontWeight="medium">
                {grn.grnNumber}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderWidth: 2 }} />

          {/* GRN Details Grid */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Purchase Order
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                  {grn.purchaseOrder?.poNumber}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Received Date
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>
                  {new Date(grn.receiptDate).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Supplier
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                  {grn.supplier?.name}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Status
                </Typography>
                <Chip
                  label={grn.status.toUpperCase()}
                  color="success"
                  size="large"
                  sx={{ 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    height: '32px'
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderWidth: 2 }} />

          {/* Items Table Section */}
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3, fontSize: '1.8rem' }}>
            Received Items
          </Typography>

          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'divider'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Item Details
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Batch Number
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Quantity
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Unit Price
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grn.items?.map((item, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                        {item.item?.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        {item.item?.brand}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                        {item.batchNumber}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                        {item.receivedQuantity || item.acceptedQuantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                        ₹{item.unitCost?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'primary.main' }}>
                        ₹
                        {(
                          (item.receivedQuantity || item.acceptedQuantity) *
                          item.unitCost
                        ).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals Section */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ 
              minWidth: 400, 
              p: 3, 
              backgroundColor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                  Subtotal:
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  ₹{subtotal.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                  Tax (18%):
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  ₹{tax.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 2, borderWidth: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.4rem' }}>
                  Total:
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: '1.4rem' }}>
                  ₹{total.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};